import {
    AppTable,
    FlexRow,
    PaginationComponent,
    FlexBox,
    FlexRowBetween,
} from 'components'
import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useTabletAndBelowMediaQuery } from 'layout'
import { Card, Checkbox, Button, Modal } from 'semantic-ui-react'
import { AppButton, AppPrimaryButton } from 'elements'
import { DotsThreeVertical, PencilSimple, Trash, X, UserPlus, QrCode, Download, Printer } from 'phosphor-react'
import styled from 'styled-components'

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    flex-wrap: wrap;
    gap: 12px;

    .pagination-info {
        color: #666;
        font-size: 14px;
    }
`

const ActionDropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e8e9e9;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 200px;
    z-index: 1000;
    margin-top: 8px;
    padding: 4px 0;
`

const MenuItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: background-color 0.2s ease;
    font-size: 14px;
    color: ${props => props.danger ? '#dc3545' : '#333'};

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-top: 1px solid #f0f0f0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const getStatusStyle = (status) => {
    if (status === 'Active') {
        return {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
        }
    }
    if (status === 'Inactive') {
        return {
            backgroundColor: '#ffebee',
            color: '#c62828',
        }
    }
    return {
        backgroundColor: '#fff3e0',
        color: '#e65100',
    }
}

const AssetCard = ({ item, onView, onEdit, onDelete, onAssignInspection, onGenerateQR, onPrintLabel, onExport }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true)
        setDropdownOpen(false)
    }

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete(item)
        }
        setDeleteConfirmOpen(false)
    }

    return (
        <>
            <Card fluid>
                <Card.Content>
                    <Card.Header>{item.assetName || 'N/A'}</Card.Header>
                    <Card.Description>
                        <FlexRow>
                            <Card.Meta>Asset ID:</Card.Meta>&nbsp; {item.assetId || 'N/A'}
                        </FlexRow>
                        <FlexRow>
                            <Card.Meta>Category:</Card.Meta>&nbsp; {item.category || 'N/A'}
                        </FlexRow>
                        <FlexRow>
                            <Card.Meta>Location:</Card.Meta>&nbsp; {item.location || 'N/A'}
                        </FlexRow>
                        <FlexRow>
                            <Card.Meta>Status:</Card.Meta>&nbsp; {item.status || 'N/A'}
                        </FlexRow>
                        <FlexRow>
                            <Card.Meta>Assigned To:</Card.Meta>&nbsp; {item.assignedTo || 'Unassigned'}
                        </FlexRow>
                        <FlexBox className="justify-content-end mt-2 gap-2">
                            <AppButton onClick={() => {
                                if (onView) {
                                    onView(item)
                                }
                            }}>
                                View
                            </AppButton>
                            <ActionDropdownContainer ref={dropdownRef}>
                                <Button
                                    icon
                                    basic
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    style={{ padding: '8px', minWidth: 'auto' }}
                                >
                                    <DotsThreeVertical size={20} />
                                </Button>
                                {dropdownOpen && (
                                    <DropdownMenu>
                                        {onEdit && (
                                            <MenuItem onClick={() => {
                                                onEdit(item)
                                                setDropdownOpen(false)
                                            }}>
                                                <PencilSimple size={16} />
                                                <span>Edit Asset</span>
                                            </MenuItem>
                                        )}
                                        {onAssignInspection && (
                                            <MenuItem onClick={() => {
                                                onAssignInspection(item)
                                                setDropdownOpen(false)
                                            }}>
                                                <UserPlus size={16} />
                                                <span>Assign Inspection</span>
                                            </MenuItem>
                                        )}
                                        {onGenerateQR && (
                                            <MenuItem onClick={() => {
                                                onGenerateQR(item)
                                                setDropdownOpen(false)
                                            }}>
                                                <QrCode size={16} />
                                                <span>Generate QR</span>
                                            </MenuItem>
                                        )}
                                        {onPrintLabel && (
                                            <MenuItem onClick={() => {
                                                onPrintLabel(item)
                                                setDropdownOpen(false)
                                            }}>
                                                <Printer size={16} />
                                                <span>Print Label</span>
                                            </MenuItem>
                                        )}
                                        <MenuItem onClick={() => {
                                            if (onExport) {
                                                onExport(item)
                                            }
                                            setDropdownOpen(false)
                                        }}>
                                            <Download size={16} />
                                            <span>Export</span>
                                        </MenuItem>
                                        {onDelete && (
                                            <MenuItem onClick={handleDeleteClick} danger>
                                                <Trash size={16} />
                                                <span>Delete Asset</span>
                                            </MenuItem>
                                        )}
                                    </DropdownMenu>
                                )}
                            </ActionDropdownContainer>
                        </FlexBox>
                    </Card.Description>
                </Card.Content>
            </Card>
            <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} size="tiny" className="model">
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Delete Asset</span>
                        <X className="pointer" size={32} onClick={() => setDeleteConfirmOpen(false)} />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this asset?</p>
                </Modal.Content>
                <Modal.Actions>
                    <AppButton onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <AppPrimaryButton onClick={handleDeleteConfirm} style={{ backgroundColor: '#dc3545' }}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}

const ActionBoxes = ({
    item,
    onView,
    onEdit,
    onDelete,
    onAssignInspection,
    onGenerateQR,
    onPrintLabel,
    onExport,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true)
        setDropdownOpen(false)
    }

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete(item)
        }
        setDeleteConfirmOpen(false)
    }

    return (
        <>
            <FlexBox className="gap-2 align-items-center">
                <AppButton onClick={() => {
                    if (onView) {
                        onView(item)
                    }
                }}>
                    View
                </AppButton>
                <ActionDropdownContainer ref={dropdownRef}>
                    <Button
                        icon
                        basic
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        style={{ padding: '8px', minWidth: 'auto' }}
                    >
                        <DotsThreeVertical size={20} />
                    </Button>
                    {dropdownOpen && (
                        <DropdownMenu>
                            {onEdit && (
                                <MenuItem onClick={() => {
                                    onEdit(item)
                                    setDropdownOpen(false)
                                }}>
                                    <PencilSimple size={16} />
                                    <span>Edit Asset</span>
                                </MenuItem>
                            )}
                            {onAssignInspection && (
                                <MenuItem onClick={() => {
                                    onAssignInspection(item)
                                    setDropdownOpen(false)
                                }}>
                                    <UserPlus size={16} />
                                    <span>Assign Inspection</span>
                                </MenuItem>
                            )}
                            {onGenerateQR && (
                                <MenuItem onClick={() => {
                                    onGenerateQR(item)
                                    setDropdownOpen(false)
                                }}>
                                    <QrCode size={16} />
                                    <span>Generate QR</span>
                                </MenuItem>
                            )}
                            {onPrintLabel && (
                                <MenuItem onClick={() => {
                                    onPrintLabel(item)
                                    setDropdownOpen(false)
                                }}>
                                    <Printer size={16} />
                                    <span>Print Label</span>
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => {
                                if (onExport) {
                                    onExport(item)
                                }
                                setDropdownOpen(false)
                            }}>
                                <Download size={16} />
                                <span>Export</span>
                            </MenuItem>
                            {onDelete && (
                                <MenuItem onClick={handleDeleteClick} danger>
                                    <Trash size={16} />
                                    <span>Delete Asset</span>
                                </MenuItem>
                            )}
                        </DropdownMenu>
                    )}
                </ActionDropdownContainer>
            </FlexBox>
            <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} size="tiny" className="model">
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Delete Asset</span>
                        <X className="pointer" size={32} onClick={() => setDeleteConfirmOpen(false)} />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this asset?</p>
                </Modal.Content>
                <Modal.Actions>
                    <AppButton onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <AppPrimaryButton onClick={handleDeleteConfirm} style={{ backgroundColor: '#dc3545' }}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}

export const AssetList = ({
    data,
    filter,
    pageChanged,
    totalCount,
    onView,
    onEdit,
    onDelete,
    onAssignInspection,
    onGenerateQR,
    onPrintLabel,
    onExport,
    permissions = {},
}) => {
    const isMobile = useTabletAndBelowMediaQuery()

    const {
        canViewAssetModule = true,
        canEditAsset = true,
        canDeleteAsset = true,
        canAssignInspection = true,
        canGenerateQr = true,
        canPrintLabel = true,
    } = permissions

    const columns = useMemo(
        () => [
            {
                title: 'Asset ID',
                key: 'assetId',
                textAlign: 'left',
            },
            {
                title: 'Asset Name',
                key: 'assetName',
                textAlign: 'left',
            },
            {
                title: 'Category',
                key: 'category',
                textAlign: 'left',
            },
            {
                title: 'Location',
                key: 'location',
                textAlign: 'left',
            },
            {
                title: 'Status',
                key: 'status',
                textAlign: 'left',
                render: item => {
                    const statusStyle = getStatusStyle(item.status)
                    return (
                        <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            ...statusStyle,
                        }}>
                            {item.status || 'N/A'}
                        </span>
                    )
                },
            },
            {
                title: 'Assigned To',
                key: 'assignedTo',
                textAlign: 'left',
                render: item => (
                    <FlexRow className="align-items-center">
                        <Checkbox />
                        <span className="ms-2">{item.assignedTo || 'Unassigned'}</span>
                    </FlexRow>
                ),
            },
            {
                title: 'Actions',
                key: 'Actions',
                textAlign: 'center',
                render: item => (
                    canViewAssetModule ? (
                        <ActionBoxes
                            item={item}
                            onView={onView}
                            onEdit={canEditAsset ? onEdit : undefined}
                            onDelete={canDeleteAsset ? onDelete : undefined}
                            onAssignInspection={
                                canAssignInspection
                                    ? onAssignInspection
                                    : undefined
                            }
                            onGenerateQR={canGenerateQr ? onGenerateQR : undefined}
                            onPrintLabel={canPrintLabel ? onPrintLabel : undefined}
                            onExport={onExport}
                        />
                    ) : null
                ),
            },
        ],
        [
            isMobile,
            onView,
            onEdit,
            onDelete,
            onAssignInspection,
            onGenerateQR,
            onPrintLabel,
            onExport,
            canViewAssetModule,
            canEditAsset,
            canDeleteAsset,
            canAssignInspection,
            canGenerateQr,
            canPrintLabel,
        ],
    )

    const startIndex = ((filter?.PageNumber || 1) - 1) * (filter?.PageSize || 25) + 1
    const endIndex = Math.min(startIndex + (filter?.PageSize || 25) - 1, totalCount)

    const handlePageChange = (newPage) => {
        if (typeof newPage === 'number') {
            pageChanged(newPage)
        } else {
            pageChanged(newPage)
        }
    }

    return (
        <>
            <AppTable
                data={data}
                columns={columns}
                pageChanged={handlePageChange}
                filter={filter}
                totalCount={totalCount}
                emptyMessage="No assets found"
                mobileCard={
                    <AssetCard
                        onView={onView}
                        onEdit={canEditAsset ? onEdit : undefined}
                        onDelete={canDeleteAsset ? onDelete : undefined}
                        onAssignInspection={
                            canAssignInspection ? onAssignInspection : undefined
                        }
                        onGenerateQR={canGenerateQr ? onGenerateQR : undefined}
                        onPrintLabel={canPrintLabel ? onPrintLabel : undefined}
                        onExport={onExport}
                    />
                }
                hidePagination
            />
            {totalCount > 0 && (
                <PaginationWrapper>
                    <div className="pagination-info">
                        Showing {startIndex}-{endIndex} of {totalCount} assets
                    </div>
                    <div className="w-100 justify-content-end d-flex">
                        <PaginationComponent
                            activePage={filter?.PageNumber || 1}
                            pageChanged={handlePageChange}
                            pageSize={filter?.PageSize || 25}
                            totalCount={totalCount}
                        />
                    </div>
                </PaginationWrapper>
            )}
        </>
    )
}
